import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ProductDetailSkeleton from './skeletons/ProductDetailSkeleton';

const isVideo = (file) => {
  return file?.match(/\.(mp4|webm|ogg)$/i);
};

const ProductDetail = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [mainPhoto, setMainPhoto] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const { cart, addToCart } = useCart();
  const navigate = useNavigate();
  const [sliderOpen, setSliderOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const isDesktop = useMediaQuery({ query: '(min-width: 768px)' }); // Detect desktop

  const isInCart = cart.some(
    (item) => item._id === product?._id && item.size === selectedSize
  );
  const allOutOfStock = product?.sizes.every(s => s.stock === 0);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const handleTouchStart = (e) => {
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;

    // Swipe left → next
    if (distance > 50 && currentPhotoIndex < product.photos.length - 1) {
      setCurrentPhotoIndex((prev) => prev + 1);
    }

    // Swipe right → prev
    if (distance < -50 && currentPhotoIndex > 0) {
      setCurrentPhotoIndex((prev) => prev - 1);
    }

    // Reset
    setTouchStartX(null);
    setTouchEndX(null);
  };



  useEffect(() => {
    const API_BASE = '';
    axios.get(`${API_BASE}/api/products/by-product-id/${productId}`)
      .then(res => {
        const prod = res.data;
        setProduct(prod);
        setMainPhoto(prod.photos?.[0]);

        if (prod.sizes?.length > 0) {
          setSelectedSize(prod.sizes[0].size); // ✅ Auto-select first size
        }
      })
  }, [productId]);

  useEffect(() => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
    setIsFavorite(wishlist.some(item => item.productId === productId));
  }, [productId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!sliderOpen) return;
      if (e.key === 'ArrowRight' && currentPhotoIndex < product.photos.length - 1) {
        setCurrentPhotoIndex((prev) => prev + 1);
      } else if (e.key === 'ArrowLeft' && currentPhotoIndex > 0) {
        setCurrentPhotoIndex((prev) => prev - 1);
      } else if (e.key === 'Escape') {
        setSliderOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sliderOpen, currentPhotoIndex, product?.photos.length]);

  const toggleFavorite = () => {
    const wishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
    const exists = wishlist.some(item => item.productId === productId);
    const updated = exists
      ? wishlist.filter(item => item.productId !== productId)
      : [...wishlist, {
        productId: product.productId,
        name: product.name,
        price: product.price,
        weight: product.weight,
        photos: product.photos,
      }];
    localStorage.setItem('wishlist', JSON.stringify(updated));
    setIsFavorite(!exists);
    toast.success(exists ? 'Removed from Wishlist' : 'Added to Wishlist');
  };

  const handleNotifySubmit = async () => {
    if (!notifyEmail || !selectedSize) {
      toast.error('Please enter a valid email and select a size.');
      return;
    }

    try {
      await axios.post(`/api/products/notify-me/${product._id}`, {
        email: notifyEmail,
        size: selectedSize
      });

      toast.success('You’ll be notified when it’s back in stock.');
      setNotifyModalOpen(false);
      setNotifyEmail('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to save notification request.');
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) return toast.error('Please select a size');

    const maxStock = product.sizes.find(s => s.size === selectedSize)?.stock || 1;
    if (quantity > maxStock) {
      return toast.error('Quantity exceeds available stock');
    }

    addToCart({
      _id: product._id,
      productId: product.productId,
      name: product.name,
      price: product.price,
      weight: product.weight,
      photos: product.photos,
      size: selectedSize,
      quantity,
      sizes: product.sizes,
    });
  };
  const selectedStock = product?.sizes?.find(s => s.size === selectedSize)?.stock || 0;

  if (!product) return <ProductDetailSkeleton />;

  return (
    <div className="max-w-6xl mx-auto px-4 pt-24 pb-12 grid md:grid-cols-2 gap-10 items-start">
      {/* Left: Images */}
      <div>
        <div
          className="w-full object-cover rounded-lg border cursor-pointer overflow-hidden"
          onClick={() => {
            setCurrentPhotoIndex(product.photos.indexOf(mainPhoto));
            setSliderOpen(true);
          }}
        >
          {isVideo(mainPhoto) ? (
            <video
              src={`/uploads/${mainPhoto?.replace(/\\/g, '/')}`}
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            />
          ) : (
            <img
              src={`/uploads/${mainPhoto?.replace(/\\/g, '/')}`}
              alt="Main"
              className="w-full object-cover"
            />
          )}
        </div>

        <div className="flex mt-4 gap-2 overflow-x-auto">
          {product.photos.map((photo, idx) => {
            const src = `/uploads/${photo.replace(/\\/g, '/')}`;
            return (
              <div
                key={idx}
                onClick={() => setMainPhoto(photo)}
                className={`w-20 h-20 rounded border cursor-pointer overflow-hidden ${mainPhoto === photo ? 'ring-2 ring-black' : ''}`}
              >
                {isVideo(photo) ? (
                  <video src={src} className="w-full h-full object-cover" muted />
                ) : (
                  <img src={src} alt={`Thumb-${idx}`} className="w-full h-full object-cover" />
                )}
              </div>
            );
          })}

        </div>
      </div>

      {/* Right: Info */}
      <div className="space-y-6">
        {/* 21K Badge and Favorite button in one line */}
        <div className="flex justify-between items-center">
          <span className="inline-block bg-yellow-400 text-white text-sm px-2 py-1 rounded uppercase">21K</span>
          <button onClick={toggleFavorite}>
            <i className={`fa${isFavorite ? 's' : 'r'} fa-heart text-2xl ${isFavorite ? 'text-red-500' : 'text-gray-400'}`}></i>
          </button>
        </div>

        {/* Product Name clearly below */}
        <h1 className="text-3xl font-bold text-gray-800">{product.name}</h1>

        {/* Product Type clearly below name */}
        <div className="flex gap-2 flex-wrap">
          {product.productType.map((type, idx) => (
            <span key={idx} className="bg-gray-200 text-gray-700 text-sm px-2 py-1 rounded">{type}</span>
          ))}
        </div>

        {/* Product Description */}
        {/* Product Description */}
        <div className="prose prose-sm sm:prose lg:prose-lg max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: product.description }} />

        <hr className="my-4 border-gray-200" />

        <div className="flex flex-col gap-2">
          <p className="text-3xl font-bold text-gray-800">
            ${product.price.toFixed(2)}
          </p>
          <p className="text-lg text-gray-600">
            Weight: <span className="font-medium">{product.weight}g</span>
          </p>
          <p className="text-lg text-gray-600">
            21K Gold
          </p>
        </div>

        {/* Sizes */}
        {product.sizes.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2">Select Size:</h4>
            <div className="flex gap-2 flex-wrap">
              {product.sizes.map((s, idx) => {
                const isOutOfStock = s.stock === 0;
                const isSelected = selectedSize === s.size;

                return (
                  <button
                    key={idx}
                    onClick={() => s.stock > 0 && setSelectedSize(s.size)}
                    disabled={s.stock === 0}
                    title={s.stock === 0 ? 'Out of Stock' : ''}
                    className={`px-4 py-2 text-sm rounded border transition relative ${s.stock === 0
                      ? ' text-gray-400'
                      : selectedSize === s.size
                        ? 'bg-black text-white border-black'
                        : 'bg-white text-black border-gray-300 hover:border-black'
                      }`}
                  >
                    {s.size}
                  </button>
                );
              })}

            </div>
          </div>
        )}

        {/* Quantity selector */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium">Quantity:</label>
          <div className="flex border rounded overflow-hidden">
            <button
              className="px-3 py-1 text-lg"
              onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
            >–</button>
            <span className="px-4 py-1 border-x">{quantity}</span>
            <button
              className="px-3 py-1 text-lg"
              onClick={() => {
                const max = product.sizes.find(s => s.size === selectedSize)?.stock || 1;
                setQuantity(prev => (prev < max ? prev + 1 : prev));
              }}
            >+</button>
          </div>
        </div>
        {allOutOfStock && (
          <div className="text-red-600 font-medium text-sm mb-2">
            All sizes are currently sold out.
          </div>
        )}
        {allOutOfStock && (
          <button
            onClick={() => setNotifyModalOpen(true)}
            className="mt-2 w-full py-2 rounded border border-blue-500 text-blue-600 hover:bg-blue-50 transition"
          >
            Notify Me When Available
          </button>
        )}

        {/* Add to Cart */}
        <button
          onClick={() => {
            if (isInCart) {
              navigate('/cart');
            } else {
              handleAddToCart();

              // Add animation class temporarily
              const btn = document.getElementById('add-to-cart-btn');
              btn.classList.add('animate-pulse');
              setTimeout(() => btn.classList.remove('animate-pulse'), 500);
            }
          }}
          id="add-to-cart-btn"
          disabled={!selectedSize || allOutOfStock}
          className={`mt-4 w-full py-3 rounded text-white font-semibold transition duration-300 ${allOutOfStock || !selectedSize
            ? 'bg-gray-400 cursor-not-allowed'
            : isInCart
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          {allOutOfStock
            ? 'Out of Stock'
            : isInCart
              ? 'Go to Cart'
              : 'Add to Cart'}
        </button>
        {product.isCustomizable && (
          <>
            <a
              href={`https://wa.me/9647514141213?text=Hi, I want to customize the design for product ${product.name} (${product.productId})`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full py-3 rounded text-white font-semibold transition duration-300 bg-green-600 hover:bg-green-700 block text-center"
            >
              Customize the design now
            </a>

          </>
        )}
      </div>



      {notifyModalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow-lg max-w-sm w-full relative">
            <h3 className="text-lg font-semibold mb-2">Notify Me When Available</h3>
            <p className="text-sm text-gray-600 mb-4">Enter your email and choose a size. We’ll notify you when it’s back in stock.</p>

            <input
              type="email"
              placeholder="you@example.com"
              value={notifyEmail}
              onChange={(e) => setNotifyEmail(e.target.value)}
              className="w-full border border-gray-300 rounded p-2 mb-3"
            />

            <div className="mb-3">
              <h4 className="text-sm font-semibold mb-2">Select Size:</h4>
              <div className="flex flex-wrap gap-2 justify-start">
                {product.sizes.map((s, idx) => {
                  const isSelected = selectedSize === s.size;
                  const isOutOfStock = s.stock === 0;

                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedSize(s.size)} // ✅ always allow selection
                      title={isOutOfStock ? 'Currently out of stock' : ''}
                      className={`px-4 py-2 text-sm rounded border transition ${isSelected
                        ? 'bg-black text-white border-black'
                        : isOutOfStock
                          ? 'bg-gray-100 text-gray-500 border-gray-300 hover:border-gray-400'
                          : 'bg-white text-black border-gray-300 hover:border-black'
                        }`}
                    >
                      {s.size}
                    </button>
                  );
                })}
              </div>
            </div>



            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setNotifyModalOpen(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleNotifySubmit}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {sliderOpen && (
        <div
          className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center group"
          onClick={() => setSliderOpen(false)}
        >
          <div
            className="relative max-w-3xl w-full mx-auto group"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >

            {/* Image with fade animation */}
            {isVideo(product.photos[currentPhotoIndex]) ? (
              <video
                key={currentPhotoIndex}
                src={`/uploads/${product.photos[currentPhotoIndex].replace(/\\/g, '/')}`}
                className="w-full max-h-[80vh] object-contain rounded shadow-lg transition-all duration-500 ease-in-out opacity-100"
                autoPlay
                muted
                loop
                playsInline
                controls
              />
            ) : (
              <img
                key={currentPhotoIndex}
                src={`/uploads/${product.photos[currentPhotoIndex].replace(/\\/g, '/')}`}
                alt="Preview"
                className="w-full max-h-[80vh] object-contain rounded shadow-lg transition-all duration-500 ease-in-out opacity-100"
              />
            )}

            {/* Close Button (top-right inside image) */}
            <button
              onClick={() => setSliderOpen(false)}
              className="absolute top-4 right-4 z-30 bg-white/30 hover:bg-white/50 text-white text-xl p-2 rounded-full opacity-0 group-hover:opacity-100 transition duration-300"
            >
              ✖
            </button>

            {/* Arrows – appear on hover only */}
            {product.photos.length > 1 && (
              <>
                {/* Left Arrow */}
                {currentPhotoIndex > 0 && (
                  <button
                    onClick={() =>
                      setCurrentPhotoIndex((prev) =>
                        prev === 0 ? product.photos.length - 1 : prev - 1
                      )
                    }
                    className="absolute top-1/2 left-4 transform -translate-y-1/2 z-30 bg-black/30 hover:bg-white/50 text-white text-2xl p-2 rounded-full opacity-0 group-hover:opacity-100 transition duration-300"
                  >
                    ❮
                  </button>
                )}

                {/* Right Arrow */}
                {currentPhotoIndex < product.photos.length - 1 && (
                  <button
                    onClick={() =>
                      setCurrentPhotoIndex((prev) => (prev + 1) % product.photos.length)
                    }
                    className="absolute top-1/2 right-4 transform -translate-y-1/2 z-30 bg-black/30 hover:bg-white/50 text-white text-2xl p-2 rounded-full opacity-0 group-hover:opacity-100 transition duration-300"
                  >
                    ❯
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}


    </div>
  );
};

export default ProductDetail;
